import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {AVATAR_SIZE_CHAT} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {ForkygramFollowRequestData} from 'types'
import {showSnackbar, useHistory} from 'utils'
import {
  Avatar,
  Button,
  ButtonOutline,
  ConfirmationModal,
  IconVerifiedBadge,
  Paragraph,
} from 'common/components'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'

const StyledItemContainer = styled.div`
  display: flex;
  padding: ${convertUnit(12)} ${convertUnit(20)};
  align-items: center;
  gap: ${convertUnit(12)};
  justify-content: space-between;
`
const StyledTextContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(4)};
`
const StyledUsernameContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
`
const StyledIconVerifiedBadge = styled(IconVerifiedBadge)`
  display: inline-block;
  line-height: ${convertUnit(16)};
`
const StyledButtonContainer = styled.div`
  display: flex;
  gap: ${convertUnit(8)};
`
const StyledButton = styled(Button)`
  width: 100%;
`
const StyledButtonOutline = styled(ButtonOutline)`
  width: 100%;
`
const StyledParagraph = styled(Paragraph)`
  word-break: break-all;
`

interface TemplateFollowRequestItemProps {
  data: ForkygramFollowRequestData
  onRemoveRequest(item: ForkygramFollowRequestData): void
}

export default function TemplateFollowRequestItem({
  data,
  onRemoveRequest,
}: TemplateFollowRequestItemProps) {
  const {translate} = useTranslation()
  const history = useHistory()
  const {id} = useSelector('user') || {}
  const [isAccepted, setIsAccepted] = useState(data.isAccepted || false)
  const [isRequested, setIsRequested] = useState(data.is_follow_request)
  const [isFollowed, setIsFollowed] = useState(data.is_followed)
  const [unfollowModal, setUnfollowModal] = useState(false)

  const handleAcceptFollowRequest = useCallback(() => {
    requestData('giftshop_accept_follower_request', {
      data: {member_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          setIsAccepted(true)
        } else {
          showSnackbar(
            translate('global:messageError', {context: `code-${status}`}),
          )
        }
      },
    })
  }, [data.id, translate])

  const handleRejectFollowRequest = useCallback(() => {
    requestData('giftshop_delete_follower_request', {
      data: {member_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          onRemoveRequest(data)
        } else {
          showSnackbar(
            translate('global:messageError', {context: `code-${status}`}),
          )
        }
      },
    })
  }, [data, onRemoveRequest, translate])

  const handleFollowBack = useCallback(() => {
    requestData('giftshop_post_follow', {
      data: {member_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          data.is_private ? setIsRequested(true) : setIsFollowed(true)
        }
      },
    })
  }, [data.id, data.is_private])

  const handleUnfollow = useCallback(() => {
    requestData('giftshop_delete_follow', {
      data: {member_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          onRemoveRequest(data)
        }
      },
    })
  }, [data, onRemoveRequest])

  const handleNavigateToProfile = useCallback(() => {
    history.push(
      'giftshop_profile',
      {
        self: data.id === id,
        postData: {
          member_id: data.id,
          username: data.username,
          photo: data.photo,
        },
      },
      data.username,
    )
  }, [history, data.id, data.username, data.photo, id])

  const handleRenderUnfollowModal = useMemo(
    () => (
      <ConfirmationModal
        visible={unfollowModal}
        title={translate('forkygram:unfollowTitle')}
        text={translate('forkygram:unfollowDescription')}
        toggleModal={() => setUnfollowModal(false)}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:unfollow')}
        onConfirm={handleUnfollow}
      />
    ),
    [handleUnfollow, translate, unfollowModal],
  )

  return (
    <StyledItemContainer>
      {handleRenderUnfollowModal}
      <StyledTextContainer onClick={handleNavigateToProfile}>
        <Avatar src={data.photo} alt="Avatar" size={AVATAR_SIZE_CHAT} />
        <StyledUsernameContainer>
          <StyledParagraph fontSize="m" fontWeight="bold">
            {data.username}
          </StyledParagraph>
          {data.creator_status === 'verified' && (
            <StyledIconVerifiedBadge
              height={convertUnit(18)}
              width={convertUnit(24)}
            />
          )}
        </StyledUsernameContainer>
      </StyledTextContainer>
      {isAccepted ? (
        isFollowed ? (
          <StyledButtonContainer>
            <StyledButtonOutline
              label={translate('forkygram:following')}
              borderColor="gray_5"
              type="small"
              borderWidth={2}
              color="gray_5"
              onClick={() =>
                data.is_private ? setUnfollowModal(true) : handleUnfollow()
              }
            />
          </StyledButtonContainer>
        ) : isRequested ? (
          <StyledButtonContainer>
            <StyledButtonOutline
              label={translate('global:requested')}
              borderColor="gray_5"
              type="small"
              borderWidth={2}
              color="gray_5"
              onClick={handleUnfollow}
            />
          </StyledButtonContainer>
        ) : (
          <StyledButtonContainer>
            <StyledButton
              label={translate('forkygram:followBack')}
              type="small"
              onClick={handleFollowBack}
            />
          </StyledButtonContainer>
        )
      ) : (
        <StyledButtonContainer>
          <StyledButtonOutline
            label={translate('global:reject')}
            borderColor="gray_5"
            type="small"
            borderWidth={2}
            color="gray_5"
            onClick={handleRejectFollowRequest}
          />
          <StyledButton
            label={translate('global:accept')}
            type="small"
            onClick={handleAcceptFollowRequest}
          />
        </StyledButtonContainer>
      )}
    </StyledItemContainer>
  )
}
